import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { analyticsFiltersEqual, mergeAnalyticsFilters } from '@/shared/analytics/AnalyticsFilterOps';
import {
    DEFAULT_DATE_RANGE,
    isGlobalFilterField,
    type GlobalDateRange,
    type GlobalFilterSnapshot,
} from './GlobalFilterTypes';

/**
 * Holds the global filter tier — the date range plus the cross-page dimensions
 * (provider/connection, user) that persist page-to-page. Registered as a root
 * singleton so every page and the shell filter bar share one instance; the URL
 * is kept in sync with it by {@link useGlobalFilterUrlSync}. Page-specific
 * sub-filters live in their own per-page controllers, not here.
 */
@injectable()
export class GlobalFilterController extends BaseViewModel {
    private dateRangeState: GlobalDateRange = { ...DEFAULT_DATE_RANGE };
    private dimensionsState: AnalyticsFilter[] = [];
    private cachedSnapshot: GlobalFilterSnapshot | null = null;

    get dateRange(): GlobalDateRange {
        return this.dateRangeState;
    }

    get dimensions(): AnalyticsFilter[] {
        return this.dimensionsState;
    }

    /** Stable across renders until the state changes — cached so consumers' memos and URL keys don't thrash. */
    snapshot(): GlobalFilterSnapshot {
        if (this.cachedSnapshot === null) {
            this.cachedSnapshot = {
                dateRange: { ...this.dateRangeState },
                dimensions: this.dimensionsState.map((filter) => ({ field: filter.field, values: [...filter.values] })),
            };
        }
        return this.cachedSnapshot;
    }

    private emitChange(): void {
        this.cachedSnapshot = null;
        this.notifyChange();
    }

    /** The global dimensions a page understands, so a consumer can forward only what it supports. */
    dimensionsForFields(supported: AnalyticsField[]): AnalyticsFilter[] {
        const allow = new Set(supported);
        return this.dimensionsState
            .filter((filter) => allow.has(filter.field))
            .map((filter) => ({ field: filter.field, values: [...filter.values] }));
    }

    /** Replaces the whole state in one shot (used by the URL sync on navigation). No-op when unchanged. */
    hydrate(snapshot: GlobalFilterSnapshot): void {
        if (snapshotsEqual(this.snapshot(), snapshot)) return;
        this.dateRangeState = { ...snapshot.dateRange };
        this.dimensionsState = snapshot.dimensions
            .filter((filter) => isGlobalFilterField(filter.field))
            .map((filter) => ({ field: filter.field, values: [...filter.values] }));
        this.emitChange();
    }

    setDateRange(range: GlobalDateRange): void {
        this.dateRangeState = { ...range };
        this.emitChange();
    }

    addFilters(incoming: AnalyticsFilter[]): void {
        const scoped = incoming.filter((filter) => isGlobalFilterField(filter.field));
        const { filters, changed } = mergeAnalyticsFilters(this.dimensionsState, scoped);
        if (changed) {
            this.dimensionsState = filters;
            this.emitChange();
        }
    }

    setFilter(field: AnalyticsField, values: string[]): void {
        if (!isGlobalFilterField(field)) return;
        const others = this.dimensionsState.filter((filter) => filter.field !== field);
        const clean = Array.from(new Set(values.map((value) => value.trim()).filter((value) => value.length > 0)));
        this.dimensionsState = clean.length > 0 ? [...others, { field, values: clean }] : others;
        this.emitChange();
    }

    removeValue(field: AnalyticsField, value: string): void {
        this.dimensionsState = this.dimensionsState
            .map((filter) => (filter.field === field ? { field, values: filter.values.filter((entry) => entry !== value) } : filter))
            .filter((filter) => filter.values.length > 0);
        this.emitChange();
    }

    clearDimensions(): void {
        if (this.dimensionsState.length === 0) return;
        this.dimensionsState = [];
        this.emitChange();
    }

    clearAll(): void {
        this.dateRangeState = { ...DEFAULT_DATE_RANGE };
        this.dimensionsState = [];
        this.emitChange();
    }
}

export function snapshotsEqual(a: GlobalFilterSnapshot, b: GlobalFilterSnapshot): boolean {
    return dateRangeEqual(a.dateRange, b.dateRange) && analyticsFiltersEqual(a.dimensions, b.dimensions);
}

function dateRangeEqual(a: GlobalDateRange, b: GlobalDateRange): boolean {
    return a.kind === b.kind && a.preset === b.preset && a.start === b.start && a.end === b.end;
}
